/**
 * Страница установки/смены пароля (без модалки, как в админке — отдельная страница).
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { getTelegram } from "../../lib/telegram.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useSetPassword, useChangePassword } from "@/features/profile/hooks";
import { getErrorMessage } from "../../lib/graphql.ts";

export function ProfilePasswordPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { account, refresh } = useAuth();
  const hasPassword = account?.hasPassword ?? false;
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // URQL hooks
  const { setPassword, isLoading: isSettingPassword } = useSetPassword();
  const { changePassword, isLoading: isChangingPassword } = useChangePassword();

  const isLoading = isSettingPassword || isChangingPassword;

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => navigate(-1);
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  // Пароль можно ставить только после добавления и подтверждения email
  useEffect(() => {
    if (account === undefined) return;
    if (!account?.email) {
      navigate("/profile/email", { replace: true });
    }
  }, [account?.email, navigate]);

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  const validate = useCallback((): string | null => {
    if (newPassword.length < 8) return "Пароль должен быть не менее 8 символов";
    if (newPassword !== confirmPassword) return "Пароли не совпадают";
    if (hasPassword && !currentPassword) return "Введите текущий пароль";
    return null;
  }, [newPassword, confirmPassword, currentPassword, hasPassword]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);
      try {
        if (hasPassword) {
          await changePassword(currentPassword, newPassword);
        } else {
          await setPassword(newPassword);
        }
        await refresh();
        navigate("/profile/settings", { replace: true });
      } catch (err) {
        if (err instanceof Error) {
          if (err.message.includes("incorrect") || err.message.includes("неверный"))
            setError("Неверный текущий пароль");
          else if (err.message.includes("8 characters") || err.message.includes("8 символов"))
            setError("Пароль должен быть не менее 8 символов");
          else if (err.message.includes("verified") || err.message.includes("подтвержд"))
            setError("Сначала подтвердите email");
          else setError(getErrorMessage(err, "Произошла ошибка"));
        } else setError("Произошла ошибка");
      }
    },
    [hasPassword, currentPassword, newPassword, validate, refresh, navigate, changePassword, setPassword]
  );

  const isValid =
    newPassword.length >= 8 &&
    newPassword === confirmPassword &&
    (!hasPassword || currentPassword.length > 0);

  const EyeIcon = ({ show }: { show: boolean }) =>
    show ? (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
      </svg>
    ) : (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    );

  return (
    <Layout>
      <h1 className="page-title">
        {hasPassword ? "Сменить пароль" : "Установить пароль"}
      </h1>
      <p className="text-[13px] text-slate-500 mb-4">
        {hasPassword
          ? "Введите текущий и новый пароль"
          : "Пароль позволит входить без кода из email"}
      </p>

      <div className="rounded-2xl bg-white/95 shadow-sm p-4">
        <form onSubmit={handleSubmit}>
          {hasPassword && (
            <label className="block mb-4">
              <span className="text-sm font-medium text-slate-700 mb-1.5 block">
                Текущий пароль
              </span>
              <div className="relative">
                <input
                  ref={firstInputRef}
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Введите текущий пароль"
                  required
                  disabled={isLoading}
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  <EyeIcon show={showCurrentPassword} />
                </button>
              </div>
            </label>
          )}

          <label className="block mb-4">
            <span className="text-sm font-medium text-slate-700 mb-1.5 block">
              {hasPassword ? "Новый пароль" : "Пароль"}
            </span>
            <div className="relative">
              <input
                ref={hasPassword ? undefined : firstInputRef}
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Минимум 8 символов"
                required
                minLength={8}
                disabled={isLoading}
                className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                <EyeIcon show={showNewPassword} />
              </button>
            </div>
            {newPassword.length > 0 && newPassword.length < 8 && (
              <p className="text-xs text-amber-600 mt-1">
                Ещё {8 - newPassword.length} символов
              </p>
            )}
          </label>

          <label className="block mb-4">
            <span className="text-sm font-medium text-slate-700 mb-1.5 block">
              Подтвердите пароль
            </span>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Повторите пароль"
                required
                disabled={isLoading}
                className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                <EyeIcon show={showConfirmPassword} />
              </button>
            </div>
            {confirmPassword.length > 0 && newPassword !== confirmPassword && (
              <p className="text-xs text-red-600 mt-1">Пароли не совпадают</p>
            )}
          </label>

          {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

          <button
            type="submit"
            disabled={isLoading || !isValid}
            className="btn-primary w-full"
          >
            {isLoading
              ? "Сохранение..."
              : hasPassword
                ? "Сменить пароль"
                : "Установить пароль"}
          </button>
        </form>
      </div>
    </Layout>
  );
}
